import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Heading2, BodyText, Heading3 } from './ui';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';

interface DestinationTile {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  image: string;
  destinations: string[];
  state: string;
  shortDescription?: string;
}

interface DestinationsTilesPageProps {
  onBack: () => void;
  onSelectDestination?: (destination: DestinationTile) => void;
  onNavigateTo?: (page: string, data?: any) => void;
}

const PageContainer = styled.div`
  background: #FFFFFF;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: 1.6;
  min-height: 100vh;
  position: relative;
`;

const BackButton = styled.button`
  position: fixed;
  top: 100px;
  left: ${getSpacing('xl')};
  background: white;
  border: 1px solid ${getColor('primary.100')};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: ${getColor('primary.600')};
  transition: all ${DesignSystem.motion.duration.fast} ease;
  z-index: 100;
  box-shadow: ${getShadow('medium')};
  
  &:hover {
    background: ${getColor('primary.50')};
    transform: scale(1.1);
    color: ${getColor('primary.700')};
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${getSpacing('lg')};
  width: 100%;
`;

const ContentWrapper = styled.div`
  padding-top: 150px;
  padding-bottom: ${getSpacing('3xl')};
`;

const SectionTitle = styled(Heading2)`
  margin-bottom: ${getSpacing('2xl')};
  color: ${getColor('primary.900')};
  text-align: center;
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${getSpacing('2xl')};
  margin-bottom: ${getSpacing('3xl')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('xl')};
  }
`;

const TileContainer = styled.div`
  position: relative;
  height: 350px;
  border-radius: ${DesignSystem.borderRadius.xl};
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${getShadow('subtle')};
  transition: all ${DesignSystem.motion.duration.normal} ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${getShadow('large')};
  }
`;

const TileImage = styled.div<{ bgImage: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.bgImage}) center/cover;
  background-size: cover;
  background-position: center;
  transition: transform ${DesignSystem.motion.duration.normal} ease;

  ${TileContainer}:hover & {
    transform: scale(1.05);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%);
    opacity: 0;
    transition: opacity ${DesignSystem.motion.duration.normal} ease;
  }
`;

const TileContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${getSpacing('xl')};
  color: white;
  background: linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.3));
  transform: translateY(30px);
  opacity: 0;
  transition: all ${DesignSystem.motion.duration.normal} ease;
  z-index: 2;

  ${TileContainer}:hover & {
    transform: translateY(0);
    opacity: 1;
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6));
  opacity: 0;
  transition: opacity ${DesignSystem.motion.duration.normal} ease;
  z-index: 1;

  ${TileContainer}:hover & {
    opacity: 1;
  }
`;

const TileLabel = styled.div`
  display: inline-block;
  background: ${getColor('accent.500')};
  color: white;
  padding: ${getSpacing('xs')} ${getSpacing('sm')};
  border-radius: ${DesignSystem.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: ${getSpacing('sm')};
`;

const TileTitle = styled(Heading3)`
  color: white;
  margin-bottom: ${getSpacing('sm')};
  line-height: 1.2;
`;

const TileDescription = styled(BodyText)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all ${DesignSystem.motion.duration.normal} ease;
  z-index: 1000;
  padding: ${getSpacing('xl')};
`;

const ModalContent = styled.div<{ isOpen: boolean }>`
  background: white;
  border-radius: ${DesignSystem.borderRadius['3xl']};
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: transform ${DesignSystem.motion.duration.normal} ease;
  box-shadow: ${getShadow('luxe')};
  display: flex;
`;

const ModalImage = styled.div<{ bgImage: string }>`
  flex: 0 0 50%;
  background: url(${props => props.bgImage}) center/cover;
  border-radius: ${DesignSystem.borderRadius['3xl']} 0 0 ${DesignSystem.borderRadius['3xl']};
  min-height: 400px;
  position: relative;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    border-radius: ${DesignSystem.borderRadius['3xl']} ${DesignSystem.borderRadius['3xl']} 0 0;
    min-height: 250px;
  }
`;

const ModalTextContent = styled.div`
  flex: 0 0 50%;
  padding: ${getSpacing('2xl')};
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    padding: ${getSpacing('xl')};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${getSpacing('lg')};
  right: ${getSpacing('lg')};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${getColor('primary.600')};
  transition: all ${DesignSystem.motion.duration.fast} ease;
  z-index: 10;

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const ModalLabel = styled(TileLabel)`
  align-self: flex-start;
`;

const ModalTitle = styled(Heading2)`
  color: ${getColor('primary.900')};
  margin-bottom: ${getSpacing('xl')};
  line-height: 1.2;
`;

const ModalDescription = styled(BodyText)`
  color: ${getColor('neutral.gray.700')};
  margin-bottom: ${getSpacing('xl')};
  line-height: 1.6;
`;

const DestinationsTilesPage: React.FC<DestinationsTilesPageProps> = ({ onBack, onSelectDestination, onNavigateTo }) => {
  const [selectedTile, setSelectedTile] = useState<DestinationTile | null>(null);
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  const destinations: DestinationTile[] = [
    // ============ LADAKH ============
    {
      id: 1,
      title: "Leh",
      description: "The capital city of Ladakh with stunning monasteries and vibrant markets.",
      shortDescription: "Capital city with culture & history",
      category: "Urban",
      duration: "3-4 days",
      image: "/ladakh/leh.jpg",
      destinations: ["Leh Palace", "Shanti Stupa", "Leh Market"],
      state: "ladakh"
    },
    {
      id: 2,
      title: "Nubra Valley",
      description: "Experience the unique double-humped Bactrian camels and pristine landscapes.",
      shortDescription: "Double-humped camels & pristine beauty",
      category: "Adventure",
      duration: "2-3 days",
      image: "/ladakh/nubra_valley.jpg",
      destinations: ["Diskit Monastery", "Hundur Sands", "Khali Tso"],
      state: "ladakh"
    },
    {
      id: 3,
      title: "Pangong Lake",
      description: "A high-altitude salt lake known for its stunning color changes throughout the day.",
      shortDescription: "Stunning color-changing alpine lake",
      category: "Nature",
      duration: "2-3 days",
      image: "/ladakh/pangong_lake.jpg",
      destinations: ["Pangong Tso", "Tanglang La", "Spangmik"],
      state: "ladakh"
    },
    {
      id: 4,
      title: "Khardung La",
      description: "One of the world's highest motorable passes with breathtaking views.",
      shortDescription: "World's highest motorable mountain pass",
      category: "Adventure",
      duration: "1 day",
      image: "/ladakh/khardung_la.jpg",
      destinations: ["Khardung La Pass", "Nubra Valley View", "Photography Spots"],
      state: "ladakh"
    },
    {
      id: 5,
      title: "Tso Moriri",
      description: "A stunning alpine lake surrounded by snow-capped mountains and wildlife.",
      shortDescription: "Alpine lake with pristine surroundings",
      category: "Nature",
      duration: "2-3 days",
      image: "/ladakh/tso_moriri.jpg",
      destinations: ["Tso Moriri Lake", "Wildlife Sanctuary", "Han Valley"],
      state: "ladakh"
    },
    {
      id: 6,
      title: "Tso Kar",
      description: "A shallow salt lake with unique mineral-rich waters and serene landscape.",
      shortDescription: "Mineral-rich shallow salt lake",
      category: "Nature",
      duration: "1-2 days",
      image: "/ladakh/tso_kar.jpg",
      destinations: ["Tso Kar Lake", "Salt Flats", "Village Life"],
      state: "ladakh"
    },
    
    // ============ KASHMIR ============
    {
      id: 7,
      title: "Srinagar",
      description: "Kashmir's capital city known for its stunning Dal Lake with iconic houseboats, beautiful gardens, and traditional Mughal architecture.",
      shortDescription: "Gateway to Kashmir with houseboats & culture",
      category: "Cultural",
      duration: "3-4 days",
      image: "/kashmir/srinagar.jpg",
      destinations: ["Dal Lake", "Floating Gardens", "Mughal Gardens"],
      state: "kashmir"
    },
    {
      id: 8,
      title: "Dal Lake",
      description: "Picturesque freshwater lake spanning 18 kilometers known for traditional shikara boats, water-based markets, lotus gardens, and migratory birds.",
      shortDescription: "Serene lake with houseboats & markets",
      category: "Nature",
      duration: "2-3 days",
      image: "/kashmir/dal_lake.jpg",
      destinations: ["Shikara Rides", "Lotus Gardens", "Floating Market"],
      state: "kashmir"
    },
    {
      id: 9,
      title: "Pahalgam Valley",
      description: "Picturesque valley town surrounded by towering pine forests and snow-capped mountains, gateway to Amarnath Cave pilgrimage.",
      shortDescription: "Pine forests & alpine meadows",
      category: "Adventure",
      duration: "2-3 days",
      image: "/kashmir/pahalgam.jpg",
      destinations: ["Kolahoi Peak", "Aru Valley", "Baisaran Meadows"],
      state: "kashmir"
    },
    {
      id: 10,
      title: "Sonamarg",
      description: "Scenic mountain destination at 7,500 feet with expansive meadows, crystalline streams, and pristine alpine scenery.",
      shortDescription: "Golden meadows & glacier views",
      category: "Nature",
      duration: "1-2 days",
      image: "/kashmir/sonamarg.jpg",
      destinations: ["Thajiwas Glacier", "Alpine Meadows", "Mountain Streams"],
      state: "kashmir"
    },
    
    // ============ HIMACHAL PRADESH ============
    {
      id: 11,
      title: "Manali",
      description: "Vibrant mountain town in the Kullu Valley nestled at 6,300 feet, known for adventure sports, handicrafts, and gateway to numerous trekking trails.",
      shortDescription: "Adventure capital with mountain views",
      category: "Adventure",
      duration: "3-4 days",
      image: "/himachal/manali.jpg",
      destinations: ["Old Manali", "Mall Road", "Hadimba Temple"],
      state: "himachal"
    },
    {
      id: 12,
      title: "Rohtang Pass",
      description: "Mountain pass at 13,050 feet connecting Kullu Valley to Spiti and Lahaul valleys, offering spectacular panoramic views of snow-capped Himalayan peaks.",
      shortDescription: "Gateway to Spiti with alpine views",
      category: "Nature",
      duration: "1 day",
      image: "/himachal/rohtang_pass.jpg",
      destinations: ["Snow-capped Peaks", "Adventure Activities", "Photography Spots"],
      state: "himachal"
    },
    {
      id: 13,
      title: "Spiti Valley",
      description: "Stark, barren high-altitude valley known for ancient monasteries, Buddhist culture, and otherworldly landscapes resembling the Tibetan plateau.",
      shortDescription: "Monastic culture & barren beauty",
      category: "Cultural",
      duration: "4-5 days",
      image: "/himachal/spiti.jpg",
      destinations: ["Key Monastery", "Kaza", "Chandratal Lake"],
      state: "himachal"
    },
    {
      id: 14,
      title: "Tirthan Valley",
      description: "Misty cedar forests, pristine river, and traditional village experiences in one of Himachal's most scenic and unspoiled valleys.",
      shortDescription: "Cedar forests & riverside beauty",
      category: "Nature",
      duration: "2-3 days",
      image: "/himachal/tirthan.jpg",
      destinations: ["Great Himalayan National Park", "Traditional Villages", "River Walks"],
      state: "himachal"
    },
    {
      id: 15,
      title: "Shimla",
      description: "Historic hill station with colonial charm, ornate architecture, and panoramic Himalayan vistas. Perfect blend of culture, history, and nature.",
      shortDescription: "Colonial charm & mall road culture",
      category: "Cultural",
      duration: "2-3 days",
      image: "/himachal/shimla.jpg",
      destinations: ["The Ridge", "Jakhoo Temple", "Christ Church"],
      state: "himachal"
    },
    {
      id: 16,
      title: "Paragliding & Adventure Hub",
      description: "Premier destination for paragliding, mountain biking, and adventure sports with stunning aerial views of the Himalayas.",
      shortDescription: "Adrenaline rush with mountain views",
      category: "Adventure",
      duration: "1-2 days",
      image: "/himachal/paragliding.jpg",
      destinations: ["Paragliding Sites", "Mountain Bike Trails", "Professional Training"],
      state: "himachal"
    }
  ];

  const handleTileClick = (destination: DestinationTile) => {
    setSelectedTile(destination);
  };

  const handleCloseModal = () => {
    setSelectedTile(null);
  };

  return (
    <PageContainer>
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="destinations"
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />
      <BackButton onClick={onBack}>←</BackButton>

      <Container>
        <ContentWrapper>
          <SectionTitle>Explore All Destinations</SectionTitle>

          <TilesGrid>
            {destinations.map((destination) => (
              <TileContainer key={destination.id} onClick={() => handleTileClick(destination)}>
                <TileImage bgImage={destination.image} />
                <HoverOverlay />
                <TileContent>
                  <TileLabel>{destination.category}</TileLabel>
                  <TileTitle>{destination.title}</TileTitle>
                  <TileDescription>{destination.shortDescription || destination.description}</TileDescription>
                </TileContent>
              </TileContainer>
            ))}
          </TilesGrid>
        </ContentWrapper>
      </Container>

      {/* Modal */}
      <ModalOverlay isOpen={!!selectedTile} onClick={handleCloseModal}>
        <ModalContent isOpen={!!selectedTile} onClick={(e) => e.stopPropagation()}>
          {selectedTile && (
            <>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
              <ModalImage bgImage={selectedTile.image} />
              <ModalTextContent>
                <ModalLabel>{selectedTile.category}</ModalLabel>
                <ModalTitle>{selectedTile.title}</ModalTitle>
                <ModalDescription>{selectedTile.description}</ModalDescription>
                <div>
                  <BodyText style={{ fontWeight: 600, color: getColor('primary.900'), marginBottom: getSpacing('sm') }}>
                    Duration: {selectedTile.duration}
                  </BodyText>
                  <BodyText style={{ color: getColor('neutral.gray.600') }}>
                    {selectedTile.destinations.join(' • ')}
                  </BodyText>
                </div>
              </ModalTextContent>
            </>
          )}
        </ModalContent>
      </ModalOverlay>
    </PageContainer>
  );
};

export default DestinationsTilesPage;
